import { useState } from 'react';
import { Modal, ModalBody, Button, Stack } from '@7shifts/sous-chef-base';
import backspaceKey from '@src/assets/icons/backspace-key.svg';
import type { TipsRequiredResponse, PunchResponse } from '@src/api/punchTypes';
import { useTranslation } from '@src/i18n';
import './TipsModal.css';

const KEY_ROWS = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9']
] as const;

// Largest tip amount the backend can accept with cent-precision and without overflow is $100,000
const MAX_TIP_CENTS = 10_000_000;

function formatCents(cents: number): string {
    const dollars = Math.floor(cents / 100);
    const remainder = cents % 100;
    return `$${dollars}.${String(remainder).padStart(2, '0')}`;
}

interface TipsModalProps {
    response: TipsRequiredResponse;
    onExecute: (execute: () => Promise<PunchResponse>) => void;
}

export function TipsModal({ response, onExecute }: TipsModalProps) {
    const [cents, setCents] = useState(0);
    const [submitted, setSubmitted] = useState(false);
    const { t } = useTranslation();

    function wouldExceedMax(digit: string): boolean {
        return cents * 10 + parseInt(digit, 10) > MAX_TIP_CENTS;
    }

    function pressDigit(digit: string) {
        if (wouldExceedMax(digit)) return;
        setCents(prev => prev * 10 + parseInt(digit, 10));
    }

    function pressClear() {
        setCents(0);
    }

    function pressBackspace() {
        setCents(prev => Math.floor(prev / 10));
    }

    function handleConfirm() {
        if (submitted) return;
        setSubmitted(true);
        onExecute(() => response.submitTips(Math.min(cents, MAX_TIP_CENTS) / 100));
    }

    function handleSkip() {
        if (submitted) return;
        setSubmitted(true);
        onExecute(() => response.submitTips(0));
    }

    return (
        <Modal testId="tips-modal">
            <ModalBody>
                <Stack space={20}>
                    <p className="tips-modal__title">{t('tips.title')}</p>
                    <p className="tips-modal__subtitle">{t('tips.subtitle')}</p>
                    <div className="tips-modal__card">
                        <div
                            className="tips-modal__display"
                            data-testid="tips-display"
                        >
                            {formatCents(cents)}
                        </div>
                        <div
                            className="tips-modal__keys"
                            role="group"
                            aria-label={t('tips.keypadAriaLabel')}
                        >
                            {KEY_ROWS.flat().map(digit => (
                                <button
                                    key={digit}
                                    type="button"
                                    className="tips-modal__key"
                                    disabled={submitted || wouldExceedMax(digit)}
                                    onClick={() => pressDigit(digit)}
                                    data-testid={`tips-key-${digit}`}
                                >
                                    {digit}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="tips-modal__key tips-modal__key--label"
                                disabled={submitted || cents === 0}
                                onClick={pressClear}
                                data-testid="tips-clear"
                            >
                                {t('pinGrid.clear')}
                            </button>
                            <button
                                type="button"
                                className="tips-modal__key"
                                disabled={submitted || wouldExceedMax('0')}
                                onClick={() => pressDigit('0')}
                                data-testid="tips-key-0"
                            >
                                0
                            </button>
                            <button
                                type="button"
                                className="tips-modal__key tips-modal__key--backspace"
                                disabled={submitted || cents === 0}
                                onClick={pressBackspace}
                                data-testid="tips-backspace"
                                aria-label={t('pinGrid.backspace')}
                            >
                                <img
                                    src={backspaceKey}
                                    alt=""
                                    className="tips-modal__backspace-img"
                                />
                            </button>
                        </div>
                    </div>
                    <div className="tips-modal__actions">
                        <Button
                            type="button"
                            theme="primary"
                            size="full-width"
                            disabled={submitted}
                            loading={submitted}
                            onClick={handleConfirm}
                            testId="tips-confirm"
                        >
                            {t('tips.confirm')}
                        </Button>
                        <Button
                            type="button"
                            theme="hollow"
                            size="full-width"
                            disabled={submitted}
                            onClick={handleSkip}
                            testId="tips-skip"
                        >
                            {t('tips.skip')}
                        </Button>
                    </div>
                </Stack>
            </ModalBody>
        </Modal>
    );
}
